<template>
  <b-card
      header-tag="header"
      class="mb-2">

    <div slot="header">
      <div class="float-right">
        <b-btn
            size="sm"
            variant="outline-warning"
            @click="$emit('usedPushTheLimit', roll.id)"
            v-if="isHitsMode && !isAnimating && roll.edgeMode === EdgeMode.NONE"
            v-b-tooltip.html.window.hover title="<h6>Push the Limit</h6>Spend a point of Edge to roll dice equal to your Edge, re-rolling any new sixes for extra hits, and ignore the limit.">
          <font-awesome-icon icon="chevron-double-up" fixed-width />
        </b-btn>
        <b-btn
            size="sm"
            variant="outline-warning"
            @click="$emit('usedSecondChance', roll.id)"
            v-if="isHitsMode && !isAnimating && roll.edgeMode === EdgeMode.NONE"
            v-b-tooltip.html.window.hover title="<h6>Second Chance</h6>Spend a point of Edge to re-roll dice that didn't score a hit; doesn't negate glitches.">
          <font-awesome-icon icon="redo" fixed-width />
        </b-btn>
        <b-btn
            size="sm"
            variant="outline-warning"
            @click="$emit('usedCloseCall', roll.id)"
            v-if="isHitsMode && !isAnimating && roll.edgeMode === EdgeMode.NONE && glitchLevel > 0"
            v-b-tooltip.html.window.hover title="<h6>Close Call</h6>Negate the effects of a glitch, or turn a critical glitch into a regular glitch.">
          <font-awesome-icon icon="tint" fixed-width />
        </b-btn>
        <b-btn
            size="sm"
            variant="danger"
            @click="$emit('deletedRoll', roll.id)"
            v-b-tooltip.window.hover title="Delete this roll">
          <font-awesome-icon icon="times" fixed-width />
        </b-btn>
      </div>
      <span
          class="roll-label pt-1"
          v-b-tooltip.window.hover.left :title="date"
          @mouseenter="showEditIcon = true"
          @mouseleave="showEditIcon = false"
          @click="startEditLabel">
        <span
            v-if="isEditingLabel"
            class="float-left">
          <b-form-input
              @blur.native="endEditLabel"
              @keyup.native.enter="endEditLabel"
              v-model="roll.label"
              ref="labelInput" />
        </span>
        <span v-else>
          {{roll.label}}
          <span
              class="text-muted"
              v-if="showEditIcon">
            <font-awesome-icon icon="pen" transform="shrink-3 up-2" />
          </span>
        </span>
      </span>
    </div>

    <b-row>
      <b-col class="pr-md-2" md="7">

        <h1>
          <b-badge
              v-if="isAnimating"
              variant="info"
              class="align-top text-uppercase">
            ROLLING...
          </b-badge>
          <b-badge
              v-if="!isAnimating && !isHitsMode"
              variant="success"
              class="align-top text-uppercase">
            {{sum}}
          </b-badge>
          <b-badge
              v-if="!isAnimating && isHitsMode && (totalHits > 0 || glitchLevel === 0)"
              variant="success"
              class="align-top text-uppercase">
            {{limitedHits}} {{limitedHits | pluralize('hit')}}
          </b-badge>
          <b-badge
              v-if="!isAnimating && isHitsMode && glitchLevel === 1"
              variant="danger"
              class="align-top text-uppercase">
            GLITCH
          </b-badge>
          <b-badge
              v-if="!isAnimating && isHitsMode && glitchLevel === 2"
              variant="danger"
              class="align-top text-uppercase">
            CRITICAL GLITCH
          </b-badge>
        </h1>

        <span class="dice">
          <div class="dice-group"
              v-for="(group, groupIndex) in roll.dice"
              v-if="!animate || groupIndex <= revealedGroup"
              :key="groupIndex">
            <span
                class="die-plus"
                v-if="groupIndex > 0">
              <font-awesome-icon :icon="['far', 'plus']" />
            </span><die
                v-for="(value, dieIndex) in group"
                :key="dieIndex"
                :frame-length="dieFrameLength"
                :no-highlight="!isHitsMode"
                :highlight-one="!(roll.edgeMode == EdgeMode.SECOND_CHANCE && groupIndex == 0)"
                :highlight-six="roll.edgeMode == EdgeMode.PUSH_LIMIT_PRE || roll.edgeMode == EdgeMode.PUSH_LIMIT_POST && groupIndex > 0"
                :rolling="animate && !(groupIndex < revealedGroup || (dieIndex < revealedDie && groupIndex <= revealedGroup))"
                :value="value"
                @onFrame="revealDie" />
          </div>
        </span>

      </b-col>

      <b-col class="pl-md-0" md="5">
        <div class="roll-sidebar">
          <!-- Roll settings -->
          <span class="font-weight-bold">Roll:</span>

          <span v-b-tooltip.hover="'Dice pool: ' + roll.dicePool + (roll.edgeMode === EdgeMode.PUSH_LIMIT_PRE ? ' + ' + roll.edge + ' from Push the Limit' : '')">
            <font-awesome-icon icon="clone" fixed-width />
            {{roll.dicePool}}
            <span
                v-if="roll.edgeMode === EdgeMode.PUSH_LIMIT_PRE"
                class="text-warning">
              + {{roll.edge}}
            </span>
          </span>

          <span
              v-if="isHitsMode"
              v-b-tooltip.hover="'Limit: ' + (roll.limit === null ? 'None' : roll.limit) + (roll.limit ? ' (ignored)' : '')">
            <font-awesome-icon icon="arrow-to-top" fixed-width />
            {{(roll.limit === null || usedPushTheLimit) ? 'N/A' : roll.limit}}
          </span>
          <span
              v-if="isSumMode"
              v-b-tooltip.hover="'Base value: ' + roll.baseValue">
            <font-awesome-icon icon="arrow-to-bottom" fixed-width />
            {{roll.baseValue}}
          </span>

          <br />

          <span v-if="!isAnimating">
            <!-- Initial result -->
            <span class="font-weight-bold">Result:</span>

            <span v-if="isHitsMode">
              <span
                  class="text-success"
                  v-b-tooltip.hover="getHitString(baseHits, roll.limit, roll.limit === null)">
                <font-awesome-icon icon="crosshairs" fixed-width />
                {{(roll.limit !== null && baseHits > roll.limit) ? roll.limit + ' (' + baseHits + ')' : baseHits}}
              </span>

              <span
                  class="text-danger"
                  v-b-tooltip.hover="getMissString(baseMisses)">
                <font-awesome-icon icon="ban" fixed-width />
                {{baseMisses}}
              </span>

              <br />

              <!-- Edge use -->
              <span v-if="roll.edgeMode !== EdgeMode.NONE">
                <span class="font-weight-bold">Edge:</span>

                <span v-b-tooltip.hover="edgeModeString">

                  <!-- Push the Limit -->
                  <span v-if="usedPushTheLimit">
                    <span class="text-warning">
                      <font-awesome-icon icon="chevron-double-up" fixed-width />
                      {{roll.edge}}
                    </span>

                    <font-awesome-icon :icon="['far', 'arrow-right']" fixed-width />

                    <span class="text-warning">
                      <font-awesome-icon icon="bomb" fixed-width />
                      {{rerollDicePool}}
                    </span>
                  </span>

                  <!-- Second Chance -->
                  <span v-if="roll.edgeMode === EdgeMode.SECOND_CHANCE">
                    <span class="text-warning">
                      <font-awesome-icon icon="redo" fixed-width />
                      {{rerollDicePool}}
                    </span>

                    <span v-if="baseMisses > 0">
                      <font-awesome-icon :icon="['far', 'arrow-right']" fixed-width />

                      <span class="text-warning">
                        <font-awesome-icon icon="ban" fixed-width />
                        -{{baseMisses}}
                      </span>
                    </span>
                  </span>

                  <!-- Close Call -->
                  <span v-if="roll.edgeMode === EdgeMode.CLOSE_CALL">
                    <span class="text-warning">
                      <font-awesome-icon icon="tint" fixed-width />
                    </span>

                    <span v-if="baseMisses > 0">
                      <font-awesome-icon :icon="['far', 'arrow-right']" fixed-width />

                      <span class="text-warning">
                        <font-awesome-icon icon="exclamation-triangle" />
                        <font-awesome-icon icon="level-down" />
                      </span>
                    </span>
                  </span>

                </span>
              </span>

              <br v-if="rollModified" />
              <span v-if="rollModified">
                <!-- Final result after edge -->
                <span class="font-weight-bold">Final:</span>

                <span class="text-success" v-b-tooltip.hover="getHitString(totalHits, roll.limit, usedPushTheLimit || roll.limit === null)">
                  <font-awesome-icon icon="crosshairs" fixed-width />
                  {{(roll.limit !== null && totalHits > roll.limit && !usedPushTheLimit) ? roll.limit + ' (' + totalHits + ')' : totalHits}}
                </span>

                <span class="text-danger" v-b-tooltip.hover="getMissString(totalMisses)">
                  <font-awesome-icon icon="ban" fixed-width />
                  {{totalMisses}}
                </span>
              </span>
            </span>
            <span v-else>
              {{sum - roll.baseValue}} + {{roll.baseValue}} = <span class="font-weight-bold">{{sum}}</span>
            </span>
          </span>
          <span v-else>
            <span><font-awesome-icon icon="spinner" pulse /> Rolling...</span>
          </span>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import diceTools from '../../mixins/diceTools.js'

import die from './die.vue'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

import EdgeMode from './edgeMode.js'

// Wait (in frames) before starting to reveal dice in a group
let startGroupWait = 10

// Wait (in frames) after ending a group before starting the next one
let endGroupWait = 5

export default {
  name: 'roll-info',
  mixins: [ diceTools ],
  components: { die, FontAwesomeIcon },

  data () {
    return {
      EdgeMode: EdgeMode,

      // If true, show the label edit icon
      showEditIcon: false,

      // If true, the label is being edited
      isEditingLabel: false,

      // Animation
      dieFrameLength: 1,
      nextFrameDelay: startGroupWait,
      readyToRevealDie: false,
      revealedDie: 0,
      revealedGroup: 0,
      timer: '',
      waitForNextGroup: false
    }
  },

  created () {
    this.updateTimer()
  },

  updated () {
    if (this.animate && this.revealedGroup === 0 &&
        (this.roll.edgeMode === EdgeMode.PUSH_LIMIT_POST || this.roll.edgeMode === EdgeMode.SECOND_CHANCE)) {
      // New dice are being added, so make sure we start from the second group
      this.revealedGroup = 1
    }
    this.updateTimer()
  },

  beforeDestroy () {
    clearInterval(this.timer)
  },

  props: {
    // True if this should animate the dice reveal
    animate: {
      type: Boolean,
      default: true
    },

    // Length of a "frame" of animation, i.e. length for one die reveal (in ms)
    frameTime: {
      type: Number,
      default: 100
    },

    // The roll data this box represents
    roll: {
      type: Object
    }
  },

  computed: {
    // Hits from the initial roll.
    baseHits () {
      return this.calcGroupHits(this.roll.dice[0])
    },

    // Misses from the initial roll.
    baseMisses () {
      return this.calcGroupMisses(this.roll.dice[0])
    },

    // The date in a readable format.
    date () {
      return new Date(this.roll.date).toLocaleString()
    },

    // String representing the Edge mode used.
    edgeModeString () {
      if (this.roll.edgeMode === EdgeMode.NONE) return ''

      if (this.usedPushTheLimit) {
        let preEdge = this.roll.edgeMode === EdgeMode.PUSH_LIMIT_PRE

        return 'Used Push the Limit (' + (preEdge ? 'pre' : 'post') + '-roll) for an extra ' +
          this.roll.edge + ' ' + this.$options.filters.pluralize(this.roll.edge, 'die', 'dice') +
          '; ' + this.rerollDicePool + ' ' +
          this.$options.filters.pluralize(this.rerollDicePool, 'six', 'sixes') + ' exploded'
      }

      if (this.roll.edgeMode === EdgeMode.SECOND_CHANCE) {
        let str = 'Used Second Chance to reroll ' + this.rerollDicePool + ' ' +
          this.$options.filters.pluralize(this.rerollDicePool, 'die', 'dice')

        if (this.baseMisses > 0) {
          str += ', negating ' + this.baseMisses + ' ' +
            this.$options.filters.pluralize(this.baseMisses, 'miss', 'misses')
        }

        return str
      }

      if (this.roll.edgeMode === EdgeMode.CLOSE_CALL) {
        let str = 'Used Close Call, '

        if (this.glitchLevel === 0) {
          str += 'negating the glitch'
        }

        if (this.glitchLevel === 1) {
          str += 'turning critical glitch into a regular glitch'
        }

        return str
      }

      return ''
    },

    // The glitch level (0 for no glitch, 1 for glitch, 2 for critical glitch).
    glitchLevel () {
      // Second Chance can't negate glitches
      let usedSecondChance = this.roll.edgeMode === EdgeMode.SECOND_CHANCE
      let misses = usedSecondChance ? this.baseMisses : this.totalMisses
      let hits = usedSecondChance ? this.baseHits : this.totalHits
      let dicePool = usedSecondChance ? this.roll.dicePool : this.totalDicePool

      let level = 0
      if (misses > dicePool / 2) {
        if (hits === 0) level = 2
        else level = 1

        if (this.roll.edgeMode === EdgeMode.CLOSE_CALL) --level
      }

      return level
    },

    // Whether this is still animating the roll.
    isAnimating () {
      return this.animate && this.revealedGroup < this.roll.dice.length
    },

    // Whether this is in hits mode
    isHitsMode () {
      return this.roll.mode === 'hits'
    },

    // Whether the limit was taken into account.
    isLimited () {
      return this.roll.limit != null && !this.usedPushTheLimit
    },

    // Whether this is in sum mode
    isSumMode () {
      return this.roll.mode === 'sum'
    },

    // Hits after applying the limit.
    limitedHits () {
      if (!this.isLimited) return this.totalHits

      return Math.min(this.totalHits, this.roll.limit)
    },

    // The dice that were re-rolled.
    rerolledDice () {
      if (this.roll.edgeMode === EdgeMode.PUSH_LIMIT_PRE ||
          this.roll.edgeMode === EdgeMode.SECOND_CHANCE) {
        return this.roll.dice.slice(1)
      }
      if (this.roll.edgeMode === EdgeMode.PUSH_LIMIT_POST) {
        return this.roll.dice.slice(2)
      }
      return []
    },

    // The number of dice that were re-rolled.
    rerollDicePool () {
      return this.calcDicePool(this.rerolledDice)
    },

    // The number of hits due to re-rolls.
    rerollHits () {
      return this.calcTotalHits(this.rerolledDice)
    },

    // The number of misses due to re-rolls.
    rerollMisses () {
      return this.calcTotalMisses(this.rerolledDice)
    },

    // True if edge was used to modify the roll.
    rollModified () {
      return this.usedPushTheLimit || this.roll.edgeMode === EdgeMode.SECOND_CHANCE
    },

    // Sum of all die values.
    sum () {
      return this.calcSum(this.roll.dice) + parseInt(this.roll.baseValue)
    },

    // Total hits, ignoring the limit.
    totalHits () {
      return this.calcTotalHits(this.roll.dice)
    },

    // Total misses.
    totalMisses () {
      if (this.roll.edgeMode === EdgeMode.SECOND_CHANCE) {
        return this.calcTotalMisses(this.roll.dice.slice(1))
      }

      return this.calcTotalMisses(this.roll.dice)
    },

    // Total dice pool including rerolls.
    totalDicePool () {
      return this.calcDicePool(this.roll.dice)
    },

    // Whether Push the Limit was used.
    usedPushTheLimit () {
      const pushLimitModes = [ this.EdgeMode.PUSH_LIMIT_PRE, this.EdgeMode.PUSH_LIMIT_POST ]
      return pushLimitModes.includes(this.roll.edgeMode)
    }
  },

  methods: {
    // Called when label editing is done and should be saved
    endEditLabel () {
      this.isEditingLabel = false
      this.showEditIcon = false
    },

    // Generate string to describe hits
    getHitString (hits, limit, ignoreLimit) {
      // Limit ignored, show the full hits
      if (ignoreLimit) {
        return hits + ' ' + this.$options.filters.pluralize(hits, 'hit')
      }

      let limitedHits = Math.min(hits, limit)
      let str = limitedHits + ' ' + this.$options.filters.pluralize(limitedHits, 'hit')
      if (hits > limit) {
        str += ' (' + hits + ' before limit)'
      }

      return str
    },

    // Generate string to describe misses
    getMissString (misses) {
      return misses + ' ' + this.$options.filters.pluralize(misses, 'miss', 'misses')
    },

    // Animate the dice reveal.
    onAnimate () {
      if (this.revealedGroup >= this.roll.dice.length) return

      // If we're being delayed, count down the delay
      if (this.nextFrameDelay) {
        --this.nextFrameDelay
        return
      }

      if (this.waitForNextGroup) {
        // Move to the next group
        this.revealedDie = 0
        ++this.revealedGroup

        this.nextFrameDelay = startGroupWait
        this.waitForNextGroup = false

        return
      }

      this.readyToRevealDie = true
    },

    // Reveal the next die if ready
    revealDie () {
      if (!this.readyToRevealDie) return

      ++this.revealedDie

      this.dieFrameLength = 1

      // Delay the last few dice and slow down their rolls to add a little more tension
      let groupDice = this.roll.dice[this.revealedGroup].length

      if (this.revealedDie === groupDice - 1) {
        this.nextFrameDelay = 10
        this.dieFrameLength = 8
      }
      else if (this.revealedDie === groupDice - 2) {
        this.nextFrameDelay = 6
        this.dieFrameLength = 6
      }
      else if (this.revealedDie === groupDice - 3) {
        this.nextFrameDelay = 5
        this.dieFrameLength = 4
      }
      else if (this.revealedDie >= groupDice - 6) {
        this.nextFrameDelay = 2
        this.dieFrameLength = 2
      }

      // Check if we've hit the end of this group
      if (this.revealedDie >= groupDice) {
        // Start the countdown to display the next group
        this.nextFrameDelay = endGroupWait
        this.waitForNextGroup = true
      }

      this.readyToRevealDie = false
    },

    // Called when the label should enter edit mode
    startEditLabel () {
      this.isEditingLabel = true
      this.$nextTick(function () {
        this.$refs.labelInput.focus()
      })
    },

    // Set/clear the timer interval.
    updateTimer () {
      // Set timer if it should be on
      if (this.isAnimating && !this.timer) {
        this.timer = setInterval(this.onAnimate, this.frameTime)
      }
      // Clear timer if it should be off
      else if (!this.isAnimating && this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// roll-info.vue?6aa67a6e